import { useState, useEffect, useCallback } from 'react';
import { usersApi, type UserWithDetails, type CreateUserRequest, type CreatableRole } from '../services/api/endpoints/users';
import { useAuthStore } from '../stores/authStore';
import type { UserAccessLevel, UserBusinessUnitAccess } from '../types/auth';
import type { UserFormData } from '../components/users/UserForm';

export type { UserWithDetails };

/**
 * Adapter pour mapper les données du backend vers le format attendu par le frontend
 * Le backend renvoie: { data: [...users] }
 */
function adaptBackendUser(backendUser: any): UserWithDetails {
  return {
    id: backendUser.id || '',
    email: backendUser.email || '',
    firstName: backendUser.firstName || '',
    lastName: backendUser.lastName || '',
    name: backendUser.name || `${backendUser.firstName || ''} ${backendUser.lastName || ''}`.trim() || 'Utilisateur',
    role: backendUser.role || 'viewer',
    department: backendUser.department || backendUser.position || 'Non défini',
    lastLogin: backendUser.lastLogin || backendUser.lastLoginAt || '',
    status: backendUser.status || 'active',
    avatar: backendUser.avatar,
    // Nouveaux champs alignés avec le backend
    phone: backendUser.phone,
    position: backendUser.position,
    businessUnitId: backendUser.businessUnitId,
    businessUnitType: backendUser.businessUnitType,
    // Legacy fields (pour compatibilité)
    accessLevel: backendUser.businessUnitType || backendUser.accessLevel || 'company',
    primaryBusinessUnitId: backendUser.businessUnitId || backendUser.primaryBusinessUnitId,
    permissions: backendUser.permissions,
    auth0Id: backendUser.auth0Id,
  };
}

export function useUsers() {
  const [users, setUsers] = useState<UserWithDetails[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Récupérer l'utilisateur courant et l'organisation depuis le store global
  const currentUser = useAuthStore(state => state.user);
  const organization = useAuthStore(state => state.organization);

  const loadUsers = useCallback(async (params?: {
    businessUnitId?: string;
    accessLevel?: UserAccessLevel;
  }) => {
    try {
      setLoading(true);
      setError(null);
      console.log('👥 useUsers: Loading users from API...');
      
      const response = await usersApi.getUsers(params);
      console.log('👥 useUsers: API response:', response);
      
      if (response.success && response.data) {
        // Le backend peut renvoyer soit { users: [...] } soit directement [...]
        let usersData: any[];
        
        if (Array.isArray(response.data)) {
          // Format direct: { data: [...users] }
          usersData = response.data;
        } else if (response.data.users && Array.isArray(response.data.users)) {
          // Format avec wrapper: { data: { users: [...], pagination: {...} } }
          usersData = response.data.users;
        } else {
          console.warn('👥 useUsers: Format de données inattendu:', response.data);
          usersData = [];
        }
        
        // Adapter les données du backend
        const adaptedUsers = usersData.map(adaptBackendUser);
        console.log('👥 useUsers: Loaded', adaptedUsers.length, 'users');
        
        setUsers(adaptedUsers);
      } else {
        console.warn('👥 useUsers: API response not successful:', response);
        throw new Error(response.error || 'Erreur lors du chargement des utilisateurs');
      }
    } catch (err) {
      console.error('❌ useUsers: Error loading users:', err);
      setError(err instanceof Error ? err.message : 'Erreur lors du chargement des utilisateurs');
      setUsers([]); // Ne pas utiliser de données mockées en production
    } finally {
      setLoading(false);
    }
  }, []);

  // Charger les utilisateurs au montage
  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const addUser = useCallback(async (userData: UserFormData) => {
    try {
      console.log('👥 useUsers: Creating user with data:', userData);
      
      // Récupérer l'organizationId depuis les données de l'admin connecté
      const organizationId = organization?.id || currentUser?.organizationId;
      
      if (!organizationId) {
        throw new Error('Organization ID non disponible. Veuillez vous reconnecter.');
      }
      
      // Construire le payload aligné avec CreateUserDto du backend
      const createUserPayload: CreateUserRequest = {
        email: userData.email,
        firstName: userData.firstName,
        lastName: userData.lastName,
        role: userData.role as CreatableRole,
        organizationId: organizationId, // Requis par le backend
        businessUnitId: userData.businessUnitId,
        businessUnitType: userData.businessUnitType,
        phone: userData.phone,
        position: userData.position,
        permissions: userData.permissions,
      };

      console.log('👥 useUsers: Sending payload to API:', createUserPayload);
      const response = await usersApi.createUser(createUserPayload);

      if (response.success && response.data) {
        const adaptedUser = adaptBackendUser(response.data);
        setUsers(prev => [...prev, adaptedUser]);
        console.log('👥 useUsers: User created:', adaptedUser.id);
        return adaptedUser;
      } else {
        throw new Error(response.error || 'Erreur lors de la création de l\'utilisateur');
      }
    } catch (err) {
      console.error('❌ useUsers: Error creating user:', err);
      throw err;
    }
  }, [organization, currentUser]);

  const updateUser = useCallback(async (id: string, userData: Partial<UserFormData>) => {
    try {
      console.log('👥 useUsers: Updating user:', id, userData);
      const response = await usersApi.updateUser(id, {
        firstName: userData.firstName,
        lastName: userData.lastName,
        role: userData.role as CreatableRole,
        phone: userData.phone,
        position: userData.position,
        businessUnitId: userData.businessUnitId,
        businessUnitType: userData.businessUnitType,
        avatar: userData.avatar,
      });

      if (response.success && response.data) {
        const adaptedUser = adaptBackendUser(response.data);
        setUsers(prev => prev.map(user => 
          user.id === id ? adaptedUser : user
        ));
        console.log('👥 useUsers: User updated:', id);
        return adaptedUser;
      } else {
        throw new Error(response.error || 'Erreur lors de la mise à jour de l\'utilisateur');
      }
    } catch (err) {
      console.error('❌ useUsers: Error updating user:', err);
      throw err;
    }
  }, []);

  const toggleUserStatus = useCallback(async (id: string, currentStatus?: string) => {
    try {
      // Déterminer le statut actuel si non fourni
      const userStatus = currentStatus || users.find(u => u.id === id)?.status;
      const shouldActivate = userStatus === 'inactive' || userStatus === 'pending';
      
      console.log('👥 useUsers: Toggling user status:', { id, currentStatus: userStatus, action: shouldActivate ? 'activate' : 'deactivate' });
      
      let response;
      if (shouldActivate) {
        // Activer l'utilisateur - PATCH /users/{id}/activate
        response = await usersApi.activateUser(id);
        if (response.success && response.data?.data) {
          const adaptedUser = adaptBackendUser(response.data.data);
          setUsers(prev => prev.map(user => 
            user.id === id ? adaptedUser : user
          ));
          console.log('👥 useUsers: User activated:', id);
          return adaptedUser;
        }
      } else {
        // Désactiver l'utilisateur - PUT /users/{id}/deactivate
        response = await usersApi.deactivateUser(id);
        if (response.success) {
          // Mettre à jour le statut localement car deactivate ne retourne pas l'utilisateur complet
          setUsers(prev => prev.map(user => 
            user.id === id ? { ...user, status: 'inactive' as const } : user
          ));
          console.log('👥 useUsers: User deactivated:', id);
          const updatedUser = users.find(u => u.id === id);
          return updatedUser ? { ...updatedUser, status: 'inactive' as const } : undefined;
        }
      }
      
      throw new Error(response?.error || 'Erreur lors du changement de statut');
    } catch (err) {
      console.error('❌ useUsers: Error toggling status:', err);
      throw err;
    }
  }, [users]);

  return {
    users,
    currentUser, // Utilisateur courant depuis le contexte global
    loading,
    error,
    addUser,
    updateUser,
    toggleUserStatus,
    refreshUsers: loadUsers
  };
}